import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { DEFAULT_PRIMAIR, DEFAULT_SECUNDAIR, KORDARO, PLATFORM_NAAM, contrastHex, isNietIngesteld, resolvePrimair, resolveSecundair } from '../kordaroBrand.js';

/**
 * BrandContext — whitelabel branding (fase 1b: brand-tokens).
 *
 * Twee bronnen, in volgorde van prioriteit:
 *  1. Host-based: GET /api/public/brand-context (zonder auth, via tenantResolver
 *     op hostname, bv. turnhout.heytens.app).
 *  2. Entity-based: GET /api/settings/branding van de ingelogde user (via
 *     <EntityBrandSync/> binnen AuthProvider). Zo ziet een Heytens-user óók op
 *     app.c-bridge.app zijn eigen huisstijl, en volgt de branding de winkel-switcher.
 *
 * De kleuren worden als CSS-variabelen op <html> gezet (--brand-primary, …).
 * Alle UI-kleuren lopen via die variabelen (zie index.css @theme):
 *   Tailwind:  bg-brand / text-brand / border-brand / bg-brand/10 / bg-brand-dark
 *   Inline:    style={{ color: BRAND.primary }}   (= 'var(--brand-primary)')
 *   Hex nodig: const { primaryHex } = useBrand()  (charts, <input type=color>, …)
 */

export const DEFAULT_PRIMARY = DEFAULT_PRIMAIR;
export const DEFAULT_SECONDARY = DEFAULT_SECUNDAIR;

/** CSS-var referenties — te gebruiken in inline styles en SVG-attributen. */
export const BRAND = {
  primary: 'var(--brand-primary)',
  primaryDark: 'var(--brand-primary-dark)',
  primaryLight: 'var(--brand-primary-light)',
  secondary: 'var(--brand-secondary)',
} as const;

export interface BrandInfo {
  bedrijfsNaam: string | null;
  logoUrl: string | null;
  kleurPrimair: string | null;
  kleurSecundair: string | null;
  /** Entity waarvan deze branding is (host-tenant of ingelogde winkel). null = Kordaro default. */
  entityId?: string | null;
  /** KEUKEN | RAAMDECORATIE | … — stuurt sector-terminologie (useSectorTerms). */
  sectorType?: string | null;
}

export interface BrandContextValue {
  tenant: {
    id: string;
    slug: string | null;
    host: string;
    strategy: 'SUBDOMAIN' | 'CUSTOM_DOMAIN';
  } | null;
  brand: BrandInfo;
  loading: boolean;
  /** Overschrijf branding vanuit de ingelogde entity (EntityBrandSync). null = terug naar host-branding. */
  setEntityBrand: (brand: BrandInfo | null) => void;
}

const DEFAULT_BRAND_INFO: BrandInfo = {
  bedrijfsNaam: PLATFORM_NAAM,
  logoUrl: null,
  kleurPrimair: DEFAULT_PRIMARY,
  kleurSecundair: DEFAULT_SECONDARY,
};

const DEFAULT_VALUE: BrandContextValue = {
  tenant: null,
  brand: DEFAULT_BRAND_INFO,
  loading: true,
  setEntityBrand: () => {},
};

const BrandCtx = createContext<BrandContextValue>(DEFAULT_VALUE);


// Lege kleur of het oude C-Bridge-schemagoud = niet ingesteld → Kordaro (zie kordaroBrand.ts).
function normalizeHex(v: string | null | undefined, fallback: string): string {
  return fallback === DEFAULT_SECONDARY ? resolveSecundair(v) : resolvePrimair(v);
}

function mix(hex: string, target: number, ratio: number): string {
  // target 0 = richting zwart, 255 = richting wit; ratio 0..1
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift: number) => {
    const c = (n >> shift) & 0xff;
    return Math.round(c + (target - c) * ratio);
  };
  return '#' + [ch(16), ch(8), ch(0)].map((c) => c.toString(16).padStart(2, '0')).join('').toUpperCase();
}

/** Zet de brand-tokens op <html>. Idempotent. */
export function applyBrandCssVars(primair: string | null | undefined, secundair: string | null | undefined): void {
  if (typeof document === 'undefined') return;
  const p = normalizeHex(primair, DEFAULT_PRIMARY);
  const s = normalizeHex(secundair, DEFAULT_SECONDARY);
  const root = document.documentElement.style;
  root.setProperty('--brand-primary', p);
  root.setProperty('--brand-primary-dark', mix(p, 0, 0.2));
  root.setProperty('--brand-primary-light', mix(p, 255, 0.88));
  // Tekst op een merkvlak: ivoor als dat leesbaar is (4.5:1), anders espresso (lichte klantkleur).
  root.setProperty('--brand-on-primary', contrastHex(p, KORDARO.ivory) >= 4.5 ? KORDARO.ivory : KORDARO.espresso);
  root.setProperty('--brand-secondary', s);
  root.setProperty('--brand-secondary-hover', mix(s, 255, 0.08));
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = s;
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const [hostBrand, setHostBrand] = useState<Omit<BrandContextValue, 'setEntityBrand' | 'brand'> & { brand: BrandInfo }>({
    tenant: null,
    brand: DEFAULT_BRAND_INFO,
    loading: true,
  });
  const [entityBrand, setEntityBrand] = useState<BrandInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/public/brand-context')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setHostBrand({
          tenant: data.tenant ?? null,
          brand: {
            bedrijfsNaam: data.brand?.bedrijfsNaam ?? DEFAULT_BRAND_INFO.bedrijfsNaam,
            logoUrl: data.brand?.logoUrl ?? null,
            kleurPrimair: data.brand?.kleurPrimair ?? DEFAULT_PRIMARY,
            kleurSecundair: data.brand?.kleurSecundair ?? DEFAULT_SECONDARY,
            entityId: data.tenant?.id ?? null,
            sectorType: data.brand?.sectorType ?? null,
          },
          loading: false,
        });
      })
      .catch(() => {
        if (cancelled) return;
        // Fallback op defaults bij network-fout — login blijft werken.
        setHostBrand({ tenant: null, brand: DEFAULT_BRAND_INFO, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Entity-branding (ingelogde user) wint van host-branding; host-branding wint van defaults.
  const brand: BrandInfo = entityBrand ?? hostBrand.brand;

  useEffect(() => {
    applyBrandCssVars(brand.kleurPrimair, brand.kleurSecundair);
  }, [brand.kleurPrimair, brand.kleurSecundair]);

  // PWA: manifest + iOS-icoon van de winkel (Fase 4). Zonder entity → statische Kordaro-versie.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    const touch = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
    const id = brand.entityId && brand.bedrijfsNaam && brand.bedrijfsNaam !== PLATFORM_NAAM ? brand.entityId : null;
    if (manifest) manifest.href = id ? `/api/public/pwa/manifest.webmanifest?entity=${encodeURIComponent(id)}` : '/manifest.webmanifest';
    if (touch) touch.href = id ? `/api/public/pwa/icon/${encodeURIComponent(id)}/192.png` : '/apple-touch-icon.png';
  }, [brand.entityId, brand.bedrijfsNaam]);

  const value: BrandContextValue = {
    tenant: hostBrand.tenant,
    brand,
    loading: hostBrand.loading,
    setEntityBrand,
  };

  return <BrandCtx.Provider value={value}>{children}</BrandCtx.Provider>;
}

export function useBrandContext(): BrandContextValue {
  return useContext(BrandCtx);
}

/**
 * Handige hook voor componenten die een échte hex nodig hebben
 * (charts met kleur-berekening, <input type="color">, alpha-concatenatie).
 * Voor gewone styling: gebruik Tailwind `bg-brand` of `BRAND.primary`.
 */
export function useBrand() {
  const { brand, tenant, loading } = useBrandContext();
  const primaryHex = normalizeHex(brand.kleurPrimair, DEFAULT_PRIMARY);
  const secondaryHex = normalizeHex(brand.kleurSecundair, DEFAULT_SECONDARY);
  return {
    naam: brand.bedrijfsNaam ?? PLATFORM_NAAM,
    entityId: brand.entityId ?? null,
    sectorType: brand.sectorType ?? null,
    logoUrl: brand.logoUrl,
    primaryHex,
    secondaryHex,
    primaryDarkHex: mix(primaryHex, 0, 0.2),
    isWhitelabel: tenant !== null || brand.bedrijfsNaam !== PLATFORM_NAAM,
    /** Winkel zonder eigen logo, naam of kleur → de app toont het Kordaro-platformlogo. */
    toontPlatformMerk: tenant === null && !brand.logoUrl
      && (!brand.bedrijfsNaam || brand.bedrijfsNaam === PLATFORM_NAAM)
      && isNietIngesteld(brand.kleurPrimair === DEFAULT_PRIMAIR ? null : brand.kleurPrimair),
    loading,
    ...BRAND,
  };
}
